import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikDatePicker from "@/components/forms/FormikDatePicker";
import { StyledButton } from "@/components";
import { TableWrapper, StyledTable } from "./StyledTable";
import {
  useGetMyTeamReportQuery,
  useLazyGetTeamMemberReportQuery,
} from "@/api/services/attendance/employeeTracker/EmployeeTracker";
import * as XLSX from "xlsx";

const schemaOptions = [
  { label: "All", value: "All" },
  { label: "Web", value: "Web" },
  { label: "Bio Metric", value: "Bio Metric" },
];

const validationSchema = Yup.object().shape({
  schema: Yup.string().required("Schema is required"),
  fromDate: Yup.date().required("From date is required"),
  toDate: Yup.date().required("To date is required"),
});

const MyTeamReport = () => {
  const startOfMonth = moment().startOf("month").toDate();
  const endOfMonth = moment().endOf("month").toDate();
  // moment().toDate();
  // const endOfMonth = moment().toDate();

  const [filters, setFilters] = useState({
    schema: "All",
    startDate: moment(startOfMonth).format("YYYY-MM-DD"),
    endDate: moment(endOfMonth).format("YYYY-MM-DD"),
  });
  const [rows, setRows] = useState([]);
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [fetchTeamMemberReport] = useLazyGetTeamMemberReportQuery();
  const [loadingRows, setLoadingRows] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    data: rowDetails = {},
    refetch,
    isLoading,
  } = useGetMyTeamReportQuery(filters, {
    skip: !(filters.schema && filters.startDate && filters.endDate),
  });

  const refetchData = async () => {
    setLoading(true);
    try {
      await refetch();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const prepareExcelData = (apiData) => {
    const rows = [];

    Object.entries(apiData).forEach(([departmentName, deptData]) => {
      deptData.members.forEach((emp) => {
        const dates = emp?.individualReport?.dates || {};

        // If no dates, still push one row
        if (Object.keys(dates).length === 0) {
          rows.push({
            Department: departmentName,
            "Employee ID": emp.empId,
            "Employee Name": emp.empName,
            Date: "-",

            "In Punch Count": "-",
            "Out Punch Count": "-",
            "Total Hours": "-",
            "Productive Hours": "-",
            "Break Hours": "-",

            "Present Days": emp.empPresentDayCount,
            "Avg Login Hours": emp.empAvgLoginHourse,
            "Employee Count": emp.empCount,

            "Dept Overall Count": deptData.overallEmployeeCount,
            "Dept Present Count": deptData.totalEmployeePresentCount,
            "Dept Avg Login": deptData.avgLoginHourse,
          });
          return;
        }

        Object.values(dates).forEach((day) => {
          rows.push({
            Department: departmentName,
            "Employee ID": emp.empId,
            "Employee Name": emp.empName,
            Date: day.date,

            "In Punch Count": day.inPunchCount,
            "Out Punch Count": day.outPunchCount,
            "Total Hours": day.totalHrs,
            "Productive Hours": day.productiveHrs,
            "Break Hours": day.breakHrs,

            "Present Days": emp.empPresentDayCount,
            "Avg Login Hours": emp.empAvgLoginHourse,
            "Employee Count": emp.empCount,

            "Dept Overall Count": deptData.overallEmployeeCount,
            "Dept Present Count": deptData.totalEmployeePresentCount,
            "Dept Avg Login": deptData.avgLoginHourse,
          });
        });
      });
    });

    return rows;
  };

  const downloadExcel = () => {
    const excelData = prepareExcelData(rowDetails.data); // <-- your API response object

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Attendance_Report.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (filters.schema && filters.startDate && filters.endDate) {
      refetchData();
    }
  }, [filters]);

  const loadings = isLoading || loading;

  // useEffect(() => {
  //     if (filters.schema && filters.startDate && filters.endDate) {
  //         refetch();
  //     }
  // }, [filters, refetch]);

  useEffect(() => {
    if (!rowDetails?.data) return;

    const structured = Object.entries(rowDetails.data).map(
      ([deptName, deptData]) => ({
        department: deptName,
        ...deptData,
      }),
    );

    setRows(structured);
  }, [rowDetails]);

  const toggleExpand = async (empId, rowKey) => {
    const isExpanded = !!expandedRows[rowKey];

    if (isExpanded) {
      setExpandedRows((prev) => {
        const updated = { ...prev };
        delete updated[rowKey];
        return updated;
      });
      // setLoadingRows((prev) => {
      //   const updated = { ...prev };
      //   delete updated[rowKey];
      //   return updated;
      // });
    } else {
      setLoadingRows((prev) => ({ ...prev, [rowKey]: true }));

      // try {
      //   const { data } = await fetchTeamMemberReport({
      //     empId,
      //     startDate: filters.startDate,
      //     endDate: filters.endDate,
      //   });

      //   setExpandedRows((prev) => ({ ...prev, [rowKey]: data }));
      // } catch (err) {
      //   // console.error("Error fetching details:", err);
      // } finally {
      //   setLoadingRows((prev) => ({ ...prev, [rowKey]: false }));
      // }
    }
  };

  return (
    <Box>
      {/* Filter Section */}
      <Box mb={2}>
        <Formik
          initialValues={{
            schema: "All",
            fromDate: startOfMonth,
            toDate: endOfMonth,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            setSubmitLoading(true);
            setExpandedDepartments({});
            setExpandedRows({});
            setLoadingRows({});
            setFilters({
              schema: values.schema,
              startDate: moment(values.fromDate).format("YYYY-MM-DD"),
              endDate: moment(values.toDate).format("YYYY-MM-DD"),
            });
            try {
              await refetch();
            } finally {
              setSubmitLoading(false);
            }
          }}
        >
          {({ values, resetForm }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormikSelectInput
                    name="schema"
                    label="Schema"
                    required
                    options={schemaOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormikDatePicker
                    name="fromDate"
                    label="From Date"
                    required
                    views={["year", "month", "day"]}
                    format="DD/MM/YYYY"
                    maxDate={moment().subtract(2, "days").toDate()}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormikDatePicker
                    name="toDate"
                    label="To Date"
                    required
                    views={["year", "month", "day"]}
                    format="DD/MM/YYYY"
                    // minDate={values.fromDate ? moment(values.fromDate).add(1, "days").toDate() : null}
                    minDate={
                      values.fromDate ? moment(values.fromDate).toDate() : null
                    }
                    maxDate={moment().subtract(1, "days").toDate()}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={2} justifyContent="flex-end">
                <Grid item>
                  <StyledButton
                    type="submit"
                    label="Submit"
                    isSubmitting={submitLoading}
                  />
                </Grid>
                <Grid item>
                  <StyledButton
                    sx={{
                      p: (theme) => theme.spacing(1, 2),
                      m: (theme) => theme.spacing(0, 1),
                      borderRadius: (theme) => theme.shape.s,
                      height: 40,
                    }}
                    type="reset"
                    label="Reset"
                    variant="outlined"
                    color="error"
                    size="medium"
                    onClick={() => {
                      resetForm();
                      setFilters({
                        schema: "All",
                        startDate: moment(startOfMonth).format("YYYY-MM-DD"),
                        endDate: moment(endOfMonth).format("YYYY-MM-DD"),
                      });
                      setRows([]);
                      setExpandedRows({});
                      setExpandedDepartments({});
                      setLoadingRows({});
                    }}
                  />
                </Grid>
                {rowDetails?.data &&
                  Object.keys(rowDetails.data).length > 0 && (
                    <Grid item>
                      <StyledButton
                        key="download-btn"
                        size="medium"
                        label="Download Excel"
                        onClick={() => downloadExcel()}
                      />
                    </Grid>
                  )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>

      {/* Main Table */}
      <TableWrapper>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>Department</TableCell>
              <TableCell>Employee Name</TableCell>
              <TableCell>Employee Code</TableCell>
              <TableCell>Employee Count</TableCell>
              <TableCell>Present Days</TableCell>
              <TableCell>Regularized Days</TableCell>
              <TableCell>Avg In</TableCell>
              <TableCell>Avg Out</TableCell>
              <TableCell>Avg Login Hrs</TableCell>
              <TableCell>Productive Hrs</TableCell>
              <TableCell>Break Hrs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadings ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    py={3}
                  >
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    {/* <span>Loading data...</span> */}
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              rows.map((dept) => {
                const isDeptExpanded = !!expandedDepartments[dept.department];
                return (
                  <React.Fragment key={dept.department}>
                    <TableRow
                      sx={{ backgroundColor: "#f7f6f3", cursor: "pointer" }}
                      onClick={() =>
                        setExpandedDepartments((prev) => ({
                          ...prev,
                          [dept.department]: !prev[dept.department],
                        }))
                      }
                    >
                      <TableCell>
                        <IconButton size="small">
                          {isDeptExpanded ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>{" "}
                        <strong>{dept.department}</strong>
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>{dept.overallEmployeeCount}</TableCell>
                      <TableCell>{dept.totalEmployeePresentCount}</TableCell>
                      <TableCell>
                        {dept.totalEmployeeRegularizationCount}
                      </TableCell>
                      <TableCell>{dept.avgInPunchCount}</TableCell>
                      <TableCell>{dept.avgOutPunchCount}</TableCell>
                      <TableCell>{dept.avgLoginHourse}</TableCell>
                      <TableCell>{dept.totalProductiveHousers}</TableCell>
                      <TableCell>{dept.totalBreakHousers}</TableCell>
                    </TableRow>
                    {isDeptExpanded &&
                      dept.members.map((emp, i) => {
                        const rowKey = `${dept.department}-${emp.empId || i}`;
                        const isRowExpanded = !!expandedRows[rowKey];
                        return (
                          <React.Fragment key={rowKey}>
                            <TableRow>
                              <TableCell />
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    toggleExpand(emp.empId, rowKey)
                                  }
                                >
                                  {isRowExpanded ? (
                                    <KeyboardArrowUp />
                                  ) : (
                                    <KeyboardArrowDown />
                                  )}
                                </IconButton>{" "}
                                {emp.empName}
                              </TableCell>
                              <TableCell>{emp.empId}</TableCell>
                              <TableCell>{emp.empCount}</TableCell>
                              <TableCell>{emp.empPresentDayCount}</TableCell>
                              <TableCell>{emp.empRegularizadCount}</TableCell>
                              <TableCell>{emp.empAvgInPunch}</TableCell>
                              <TableCell>{emp.empAvgOutPunch}</TableCell>
                              <TableCell>{emp.empAvgLoginHourse}</TableCell>
                              <TableCell>{emp.empProductiveHousers}</TableCell>
                              <TableCell>{emp.empBreakHourse}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ padding: "0px !important" }} />
                              <TableCell
                                colSpan={11}
                                sx={{ padding: "0px !important" }}
                              >
                                <Collapse
                                  in={isRowExpanded || loadingRows[rowKey]}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <StyledTable>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>In Count</TableCell>
                                        <TableCell>Out Count</TableCell>
                                        <TableCell>Total Hrs</TableCell>
                                        <TableCell>Productive Hrs</TableCell>
                                        <TableCell>Break Hrs</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {/* {loadingRows[rowKey] ? ( */}
                                      {/* <TableRow>
                                        <TableCell colSpan={6} align="center">
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            py={2}
                                          >
                                            <CircularProgress
                                              size={24}
                                              sx={{ color: "inherit", mr: 1 }}
                                            />
                                            <span>Loading...</span>
                                          </Box>
                                        </TableCell>
                                      </TableRow> */}
                                      {/* ) : ( */}
                                      {Object.entries(
                                        emp?.individualReport?.dates || {},
                                      ).map(([date, detail], idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{date}</TableCell>
                                          <TableCell>
                                            {detail.inPunchCount}
                                          </TableCell>
                                          <TableCell>
                                            {detail.outPunchCount}
                                          </TableCell>
                                          <TableCell>
                                            {detail.totalHrs}
                                          </TableCell>
                                          <TableCell>
                                            {detail.productiveHrs}
                                          </TableCell>
                                          <TableCell>
                                            {detail.breakHrs}
                                          </TableCell>
                                        </TableRow>
                                      ))}

                                      {/* )} */}
                                    </TableBody>
                                  </StyledTable>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </TableWrapper>
    </Box>
  );
};

export default MyTeamReport;
